/* AuthContext.js */
/* @author: anghello */

import React, { createContext, useState, useCallback, useContext } from 'react';
import Cookie from '../../services/Cookie';
import WAM from '../../services/WAM';
import PSO from '../../services/PSO';
import Strings from '../../translations/Translations';
import { toast } from 'react-toastify';

const AuthContext = createContext();

function AuthProvider({ children }) {
  const [loading, setLoading] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState(null);

  const hash = window.location.hash;
  const path = hash.split('?')[0];
  const [activeLinkTo, setActiveLinkTo] = useState(path);

  const toggleLoading = useCallback((state, message = null) => {
    setLoading(state);
    setLoadingMessage(message);
  }, []);

  const [authenticated, setAuthenticated] = useState(() => {
    let un = localStorage.getItem('rmx_username');
    let al = localStorage.getItem('rmx_authorized_login_id');
    const rmxAlbums = localStorage.getItem('rmx_albums_location');
    const rmxScripts = localStorage.getItem('rmx_scripts_location');
    let name = localStorage.getItem('rmx_name');
    const rmxEnv = process.env.REACT_APP_ENV;

    const validLocalStorageUn = (un && un.trim() !== '');
    const validLocalStorageAl = (al && al.trim() !== '');
    if (!validLocalStorageUn || !validLocalStorageAl) {
      const rmx_cookie_env = Cookie.getCookie('rmx_env');
      if (rmx_cookie_env && rmx_cookie_env === rmxEnv) {
        un = Cookie.getCookie('valid_user');
        al = Cookie.getCookie('auth_session_id');
      }
    }

    if (un && al) {
      const cookieValidUser = Cookie.getCookie('valid_user');
      const cookieAuthSessionID = Cookie.getCookie('auth_session_id');
      const cookieAlbumsLocation = Cookie.getCookie('albums_location');
      const cookieScriptsLocation = Cookie.getCookie('scripts_location');
      const cookieRmxEnv = Cookie.getCookie('rmx_env');

      if (cookieValidUser !== un) {
        Cookie.setCookie('valid_user', un, 365);
      }
      if (cookieAuthSessionID !== al) {
        Cookie.setCookie('auth_session_id', al, 365);
      }
      if (cookieAlbumsLocation !== rmxAlbums) {
        Cookie.setCookie('albums_location', rmxAlbums, 365);
      }
      if (cookieScriptsLocation !== rmxScripts) {
        Cookie.setCookie('scripts_location', rmxScripts, 365);
      }
      if (cookieRmxEnv !== rmxEnv) {
        Cookie.setCookie('rmx_env', rmxEnv, 365);
      }

      if (!name) { name = un; }
      
      return { un, al, name };
    } else {
      return false;
    }
  });

  const getUserProfile = useCallback(async ({ un, al }) => {
    try {
      const response = await WAM.getUserProfile({ username: un, al });
      if (response && response.result === 'success') {
        const data = WAM.listToJson(response.list);
        const name = (data.obj2) ? data.obj2 : un;
        localStorage.setItem('rmx_name', name);
        setAuthenticated({ un, al, name });
      } else {
        const message = (response && response.error) ? response.error : Strings('error_on_request');
        console.error(message);
      }
    } catch (err) {
      console.error(`Error: WAM.getUserProfile({ un: ${un}, al: ${al} })`)
    }
  }, []);

  const login = useCallback(async ({ username, password }) => {
    try {
      const response = await WAM.login(username, password);

      if (response.result !== 'success') {
        const message = (response.error) ? (response.error) : '';
        return Promise.reject({ message: message });
      }

      localStorage.setItem('isLogged', true);
      localStorage.setItem('rmx_username', response.username_cc.toLowerCase());
      localStorage.setItem('rmx_authorized_login_id', response.authorized_login_id);
      localStorage.setItem('rmx_albums_location', response.albums_location.replace(/(http|https):\/\//, 'http://'));
      localStorage.setItem('rmx_scripts_location', response.scripts_location.replace(/(http|https):\/\//, 'http://'));
      localStorage.setItem('rmx_name', response.username_cc.toLowerCase());

      Cookie.setCookie('valid_user', response.username_cc.toLowerCase(), 365);
      Cookie.setCookie('auth_session_id', response.authorized_login_id, 365);
      Cookie.setCookie('albums_location', response.albums_location.replace(/(http|https):\/\//, 'http://'), 365);
      Cookie.setCookie('scripts_location', response.scripts_location.replace(/(http|https):\/\//, 'http://'), 365);
      const rmxEnv = process.env.REACT_APP_ENV;
      Cookie.setCookie('rmx_env', rmxEnv, 365);

      // setAuthenticated({ un: response.username_cc, al: response.authorized_login_id });
      await getUserProfile({ un: response.username_cc, al: response.authorized_login_id });
    } catch (err) {
      if (err) {
        return Promise.reject(err);
      } else {
        return Promise.reject();
      }
    }
  }, [getUserProfile]);

  const logout = useCallback(async (doWamLogout = true) => {
    toggleLoading(true);
    const data = authenticated;
    if (data.un && data.al && doWamLogout) {
      try {
        await WAM.logout(data.un, data.al);
      } catch (err) {
        console.error(err);
      }
    }
    setAuthenticated();

    localStorage.setItem('isLogged', '');
    localStorage.setItem('rmx_username', '');
    localStorage.setItem('rmx_authorized_login_id', '');
    localStorage.setItem('rmx_albums_location', '');
    localStorage.setItem('rmx_scripts_location', '');
    localStorage.setItem('rmx_name', '');
    
    localStorage.removeItem('isLogged');
    localStorage.removeItem('rmx_username');
    localStorage.removeItem('rmx_authorized_login_id');
    localStorage.removeItem('rmx_albums_location');
    localStorage.removeItem('rmx_scripts_location');
    localStorage.removeItem('rmx_name');

    Cookie.deleteCookie('valid_user');
    Cookie.deleteCookie('auth_session_id');
    Cookie.deleteCookie('albums_location');
    Cookie.deleteCookie('scripts_location');
    Cookie.deleteCookie('rmx_env');

    toggleLoading(false);
  }, [authenticated, toggleLoading]);

  const [openModalSubscribe, setOpenModalSubscribe] = useState(false);
  const [openModalCancelSubscription, setOpenModalCancelSubscription] = useState(false);
  const [showMessageNoCredits, setShowMessageNoCredits] = useState(false);
  const [showMessageAiOperationsLimit, setShowMessageAiOperationsLimit] = useState(false);

  const [globalCreditsUserData, setGlobalCreditsUserData] = useState();
  const [globalCreditsUserBalance, setGlobalCreditsUserBalance] = useState();

  const getUserBalance = useCallback(async () => {
    try {
      const response = await PSO.getUserBalance({ un: authenticated.un, al: authenticated.al });
      if (response && response.result === 'success') {
        const { credits_bonus, credits_monthly, credits_total, credits_yearly } = response;
        setGlobalCreditsUserBalance({ credits_bonus, credits_monthly, credits_total, credits_yearly });
      } else {
        const message = (response && response.error) ? response.error : Strings('error_on_get_user_credits');
        toast.error(message);
      }
    } catch (err) {
      console.error(err);
      toast.error(`${Strings('error_on_get_user_credits')} [REQ]`);
    }
  }, [authenticated, setGlobalCreditsUserBalance]);

  const getUserDataCredits = useCallback(async () => {
    try {
      const response = await PSO.getUserDataCredits({ un: authenticated.un, al: authenticated.al });
      if (response && response.result === 'success') {
        const { status, subscriptions } = response;

        if (subscriptions && subscriptions.length) {
          // Ordering by client
          // const order = { 'HTML': 1, 'IOS': 2, 'AND': 3 };
          // subscriptions.sort((a, b) => {
          //   return order[a.client] - order[b.client];
          // });

          // Ordering by subscription type
          subscriptions.sort((a, b) => b.type - a.type);

          // Ordering by status
          subscriptions.sort((a, b) => a.status - b.status);
        }

        setGlobalCreditsUserData({ status, subscriptions });
      } else {
        const message = (response && response.error) ? response.error : Strings('error_on_get_user_credits_data');
        toast.error(message);
      }
    } catch (err) {
      console.error(err);
      toast.error(`${Strings('error_on_get_user_credits_data')} [REQ]`);
    }
  }, [authenticated, setGlobalCreditsUserData]);

  const checkCredits = useCallback(async (action, multiplier = null, secondAction = null, quickMagicImages = null) => {
    let userCanDoAction = false;
    let userCanDoAiOperations = false;
    const { un, al } = authenticated;

    if (un && al && action) {
      let transactionPrices, userBalance = 0;

      try {
        const response = await PSO.getTransactionsPrices({ un, al });
        if (response && response.result === 'success' && response.its) {
          transactionPrices = response.its;
        }
      } catch (err) {
        console.error('Error on [PSO.getTransactionsPrices]');
      }

      try {
        const response = await PSO.getUserBalance({ un, al });
        if (response && response.result === 'success') {
          userBalance = response.credits_total;
        }
      } catch (err) {
        console.error('Error on [PSO.getUserBalance]');
      }

      if (transactionPrices) {
        const targetAction = transactionPrices.filter((item) => item.feat === action)[0];

        let { price } = targetAction;
        if (action === 'createAiPhotoFast') {
          const photoAction = transactionPrices.filter((item) => item.feat === 'createAiPhoto')[0];
          if (multiplier) {
            price = (photoAction.price * multiplier) + price;
          } else {
            price = price + photoAction.price;
          }
        } else if (action === 'createRemix' && secondAction) {
          if (secondAction === 'createAiClip' || secondAction === 'quickMagic') {
            const action = transactionPrices.filter((item) => item.feat === secondAction)[0];
            price += action.price;

            if (quickMagicImages && quickMagicImages.length) {
              const photosCount = quickMagicImages.length;
              const photoCountCredits = Math.floor((photosCount - 1) / 5) + 1;
              price += (action.price * photoCountCredits);
            }
          } else if (secondAction === 'createAiPhoto' || secondAction === 'quickMagic') {
            const action = transactionPrices.filter((item) => item.feat === secondAction)[0];
            if (multiplier) { price = (action.price * multiplier) + price; }
            else { price = price + action.price; }
          } else if (secondAction === 'createAiPhotoFast') {
            const actionFast = transactionPrices.filter((item) => item.feat === secondAction)[0];
            const actionPhoto = transactionPrices.filter((item) => item.feat === 'createAiPhoto')[0];
            if (multiplier) { price += (actionPhoto.price * multiplier) + actionFast.price; }
            else { price += actionPhoto.price + actionFast.price; }
          }
        } else if (action === 'quickMagic') {
          if (quickMagicImages && quickMagicImages.length) {
            const photosCount = quickMagicImages.length;
            const photoCountCredits = Math.floor((photosCount - 1) / 5) + 1;
            price += (targetAction.price * photoCountCredits);
          }
        } else if (multiplier) {
          price = price * multiplier;
        }

        if (userBalance >= (price)) {
          userCanDoAction = true;
        }
      }

      // Check AI Operations
      try {
        const response = await PSO.getUserDataCredits({ un: authenticated.un, al: authenticated.al });
        if (response && response.result === 'success' && response.ai_operations && response.ai_operations.is_enabled) {
          userCanDoAiOperations = true;
        } else {
          userCanDoAction = false;
        }
      } catch (err) {
        console.error(err);
      }
    }

    if (!userCanDoAction) {
      if (!userCanDoAiOperations) {
        setShowMessageAiOperationsLimit(true);
      } else {
        setShowMessageNoCredits(true);
      }

      setOpenModalSubscribe(true);
    }

    return userCanDoAction;
  }, [authenticated]);

  return (
    <AuthContext.Provider
      value={{
        authenticated,
        login,
        logout,
        toggleLoading,
        loading,
        loadingMessage,
        activeLinkTo,
        setActiveLinkTo,

        checkCredits,
        openModalSubscribe,
        setOpenModalSubscribe,
        openModalCancelSubscription,
        setOpenModalCancelSubscription,
        showMessageNoCredits,
        setShowMessageNoCredits,
        showMessageAiOperationsLimit,
        setShowMessageAiOperationsLimit,

        globalCreditsUserData,
        globalCreditsUserBalance,
        getUserBalance,
        getUserDataCredits
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

function useAuth() {
  const context = useContext(AuthContext);
  return context;
}

export { useAuth, AuthProvider };
